import {
  Settings,
  Zap,
  Upload,
  Archive,
  Edit,
  ClipboardList,
  Database,
  Users,
  Layout,
  LayoutDashboard,
  ChevronDown,
  ChevronRight,
  Building2,
  DollarSign,
  FileText,
  RefreshCw,
  Check,
} from "lucide-react";

import { useLocation, useNavigate } from "react-router-dom";
import { NavLink } from "./NavLink";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  SidebarFooter,
  SidebarTrigger,
  useSidebar,
} from "../../../components/ui/sidebar";

import { useAuth } from "contexts/AuthContext";
import { useState, useEffect, useRef } from "react";
import { fetchDocsData } from "services/docsService";

const quickActionDefs = [
  {
    id: "dashboard",
    icon: LayoutDashboard,
    title: "Dashboard",
    route: "/dashboard",
  },
  {
    id: "upload",
    icon: Upload,
    title: "Upload New RFP",
    route: "/rfp-upload-and-parsing",
  },
  {
    id: "mastersManagementLayout",
    icon: Database,
    title: "Masters Management",
    route: "/masters-management",
  },
  {
    id: "assignedRequirements",
    icon: ClipboardList,
    title: "Assigned Requirements",
    route: "/assigned-requirements",
  },
  {
    id: "assignedRfps",
    icon: ClipboardList,
    title: "Assigned RFP",
    route: "/assigned-rfp",
  },
  {
    id: "personnel",
    icon: Users,
    title: "Personnel Management",
    route: "/personnel-profile-management",
  },
  {
    id: "archive",
    icon: Archive,
    title: "Previous RFP Archive",
    route: "/previous-rfp-archive",
  },
  {
    id: "templates",
    icon: Layout,
    title: "Templates",
    route: "/template-library",
  },
];

const settingsItems = [
  {
    id: "org-master",
    icon: Building2,
    title: "Org Master",
    route: "/group-organization-management?tab=groups",
  },
  {
    id: "pricing-master",
    icon: DollarSign,
    title: "Pricing Master",
    children: [
      {
        id: "burden",
        title: "Burden",
        route: "/group-organization-management?tab=burdenType",
      },
      {
        id: "discount",
        title: "Discount",
        route: "/group-organization-management?tab=discountProfile",
      },
    ],
  },
  {
    id: "change-category",
    icon: RefreshCw,
    title: "Change Category",
    route: "/group-organization-management?tab=changeCategory",
  },
];

/* Brighter text + icons */
const activeClass = "bg-indigo-600/25 text-white font-medium";
const inactiveClass = "text-slate-200 hover:bg-[#1e2130] hover:text-white";
const subActiveClass = "bg-indigo-600/15 text-white font-medium";
const subInactiveClass = "text-slate-300 hover:bg-[#1e2130] hover:text-white";

// Admin user_category_id
const ADMIN_CATEGORY_ID = 3;

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const location = useLocation();
  const navigate = useNavigate();

  const {
    allowedRoutes,
    routesLoading,
    memberships,
    selectedOrg,
    switchOrganization,
  } = useAuth();

  // ----- Admin gate -----
  // selectedOrg.categoryId is persisted from userCategoryId in AuthContext
  const isAdmin = selectedOrg?.categoryId === ADMIN_CATEGORY_ID;

  const [settingsOpen, setSettingsOpen] = useState(
    isAdmin &&
      location.pathname.includes("/group-organization-management") &&
      !location.search.includes("orgDocuments"),
  );

  const [orgDocsOpen, setOrgDocsOpen] = useState(
    isAdmin &&
      location.pathname.includes("/group-organization-management") &&
      location.search.includes("orgDocuments"),
  );

  const [pricingOpen, setPricingOpen] = useState(
    ["burdenType", "burden", "discountProfile", "discountHead"].includes(
      new URLSearchParams(location.search).get("tab"),
    ),
  );

  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  const orgSwitcherRef = useRef(null);

  const [docTitles, setDocTitles] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [docSubTypes, setDocSubTypes] = useState([]);

  const [expandedDocTitleIds, setExpandedDocTitleIds] = useState(new Set());
  const [expandedDocTypeIds, setExpandedDocTypeIds] = useState(new Set());

  // Guard active states — non-admins are never "active" on these routes
  const isOrgDocsActive =
    isAdmin &&
    location.pathname.includes("/group-organization-management") &&
    location.search.includes("orgDocuments");

  const isSettingsActive =
    isAdmin &&
    location.pathname.includes("/group-organization-management") &&
    !location.search.includes("orgDocuments");

  const hasMultipleOrgs = memberships?.length > 1;

  /* Close org dropdown when clicking outside */
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        orgSwitcherRef.current &&
        !orgSwitcherRef.current.contains(e.target)
      ) {
        setShowOrgSwitcher(false);
      }
    };
    if (showOrgSwitcher) {
      document.addEventListener("mousedown", handleOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleOutside);
    };
  }, [showOrgSwitcher]);

  /* Never keep org dropdown open after collapsing */
  useEffect(() => {
    if (collapsed) {
      setShowOrgSwitcher(false);
    }
  }, [collapsed]);

  /* Load document categories */
  useEffect(() => {
    if (orgDocsOpen && docTitles.length === 0) {
      fetchDocsData()
        .then(({ titles, types, subTypes }) => {
          setDocTitles(titles);
          setDocTypes(types);
          setDocSubTypes(subTypes);
        })
        .catch(console.error);
    }
  }, [orgDocsOpen, docTitles.length]);

  const isRouteAllowed = (route) => {
    if (!allowedRoutes?.length) return false;
    return allowedRoutes.some((allowed) => {
      const regex = new RegExp(
        "^" + allowed.replace(/\/$/, "").replace(/:\w+/g, "[^/]+") + "$",
      );
      return regex.test(route);
    });
  };

  const navItems = quickActionDefs.filter(
    (a) => a.id === "dashboard" || isRouteAllowed(a.route),
  );

  const toggleDocTitle = (titleId) => {
    setExpandedDocTitleIds((prev) => {
      const next = new Set(prev);
      next.has(titleId) ? next.delete(titleId) : next.add(titleId);
      return next;
    });
  };

  const toggleDocType = (typeId) => {
    setExpandedDocTypeIds((prev) => {
      const next = new Set(prev);
      next.has(typeId) ? next.delete(typeId) : next.add(typeId);
      return next;
    });
  };

  const selectDocScope = (params) => {
    const search = new URLSearchParams({
      tab: "orgDocuments",
      ...params,
    }).toString();
    navigate(`/group-organization-management?${search}`);
  };

  const handleOrgDocsToggle = () => {
    if (collapsed) return;
    const opening = !orgDocsOpen;
    setOrgDocsOpen(opening);
    if (opening) {
      navigate("/group-organization-management?tab=orgDocuments");
    }
  };

  const handleOrgSwitch = async (membership) => {
    if (switchingOrgId) return;
    if (membership.orgId === selectedOrg?.orgId) {
      setShowOrgSwitcher(false);
      return;
    }
    setSwitchingOrgId(membership.orgId);
    try {
      await switchOrganization(membership);
      setShowOrgSwitcher(false);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error("Organization switch failed:", err);
    } finally {
      setSwitchingOrgId(null);
    }
  };

  const activeTab = new URLSearchParams(location.search).get("tab");

  const isSettingsRouteActive = (route) => {
    const [routePath, routeSearch] = route.split("?");
    if (location.pathname !== routePath) return false;
    const routeTab = new URLSearchParams(routeSearch).get("tab");
    return activeTab === routeTab;
  };

  return (
    <Sidebar
      collapsible="icon"
      className="bg-[#0f1117] border-r border-[#1e2130]"
    >
      {/* ================================================= */}
      {/* SIDEBAR CONTENT */}
      {/* ================================================= */}
      <SidebarContent
        className="
          bg-[#0f1117]
          flex
          flex-col
          min-h-0
          overflow-hidden
        "
      >
        {/* ================================================= */}
        {/* TOP HEADER */}
        {/* ================================================= */}
        <div
          className={`
            flex-shrink-0
            px-3
            py-4
            flex
            items-center
            gap-2
            ${collapsed ? "flex-col justify-center" : ""}
          `}
        >
          {/* Logo */}
          <div
            className="
              flex
              h-8
              w-8
              items-center
              justify-center
              rounded-lg
              bg-indigo-600
              flex-shrink-0
            "
          >
            <Zap className="h-4 w-4 text-white" />
          </div>

          {!collapsed && (
            <span className="text-base font-bold text-white flex-1">
              RFP Generator
            </span>
          )}

          {/* Toggle INSIDE sidebar */}
          <SidebarTrigger
            className="
              h-8
              w-8
              flex-shrink-0
              text-slate-200
              hover:text-white
              hover:bg-white/10
              rounded-md
              transition-colors
            "
          />
        </div>

        <SidebarSeparator className="bg-[#1e2130] flex-shrink-0" />

        {/* ================================================= */}
        {/* ORGANIZATION */}
        {/* ================================================= */}
        {selectedOrg && (
          <div
            ref={orgSwitcherRef}
            className={`
              flex-shrink-0
              px-2
              py-3
              ${collapsed ? "flex justify-center" : ""}
            `}
          >
            <button
              type="button"
              onClick={() => {
                if (!collapsed && hasMultipleOrgs) {
                  setShowOrgSwitcher((prev) => !prev);
                }
              }}
              disabled={collapsed || !hasMultipleOrgs}
              title={
                hasMultipleOrgs ? "Switch organization" : selectedOrg.orgName
              }
              className={`
                ${
                  collapsed
                    ? "w-10 h-10 p-0 justify-center"
                    : "w-full px-2.5 py-2"
                }
                flex
                items-center
                gap-2
                rounded-lg
                border
                border-[#2a2e3d]
                bg-[#171a23]
                text-left
                transition-all
                ${
                  !collapsed && hasMultipleOrgs
                    ? "hover:bg-[#1e2130] hover:border-[#3a3f52] cursor-pointer"
                    : "cursor-default"
                }
              `}
            >
              <div
                className="
                  h-7
                  w-7
                  rounded-md
                  bg-indigo-600/20
                  flex
                  items-center
                  justify-center
                  flex-shrink-0
                "
              >
                <Building2 className="h-4 w-4 text-white" />
              </div>

              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-slate-300">Organization</p>
                  <p className="text-sm font-medium text-white truncate">
                    {selectedOrg.orgName}
                  </p>
                </div>
              )}

              {!collapsed && hasMultipleOrgs && (
                <ChevronDown
                  className={`
                    h-4 w-4
                    text-slate-200
                    flex-shrink-0
                    transition-transform
                    duration-200
                    ${showOrgSwitcher ? "rotate-180" : ""}
                  `}
                />
              )}
            </button>

            {/* ================================================= */}
            {/* ORG DROPDOWN */}
            {/* ================================================= */}
            {!collapsed && hasMultipleOrgs && showOrgSwitcher && (
              <div
                className="
                  mt-2
                  w-full
                  bg-[#171a23]
                  border
                  border-[#2a2e3d]
                  rounded-xl
                  shadow-lg
                  overflow-hidden
                "
              >
                <div className="px-3 py-2.5 border-b border-[#2a2e3d]">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                    Switch Organization
                  </p>
                </div>

                <div className="max-h-60 overflow-y-auto">
                  {memberships.map((membership) => {
                    const isActive = membership.orgId === selectedOrg?.orgId;
                    const isSwitching = switchingOrgId === membership.orgId;

                    return (
                      <button
                        key={membership.orgId}
                        type="button"
                        onClick={() => handleOrgSwitch(membership)}
                        disabled={!!switchingOrgId}
                        className={`
                          w-full
                          px-3
                          py-2.5
                          flex
                          items-center
                          gap-2.5
                          text-left
                          transition-colors
                          ${isActive ? "bg-indigo-600/15" : "hover:bg-[#1e2130]"}
                          disabled:opacity-60
                        `}
                      >
                        <div
                          className={`
                            h-8
                            w-8
                            rounded-lg
                            flex
                            items-center
                            justify-center
                            flex-shrink-0
                            ${isActive ? "bg-indigo-600/20" : "bg-[#222633]"}
                          `}
                        >
                          {isSwitching ? (
                            <div className="h-4 w-4 rounded-full border-2 border-indigo-400/30 border-t-indigo-400 animate-spin" />
                          ) : (
                            <Building2
                              className={`
                                h-4
                                w-4
                                ${isActive ? "text-indigo-300" : "text-slate-200"}
                              `}
                            />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p
                            className={`
                              text-sm
                              font-medium
                              truncate
                              ${isActive ? "text-white" : "text-slate-100"}
                            `}
                          >
                            {membership.orgName}
                          </p>
                          <p className="text-xs text-slate-300 truncate">
                            {membership.categoryName}
                          </p>
                        </div>

                        {isActive && (
                          <Check className="h-4 w-4 text-indigo-300 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <SidebarSeparator className="bg-[#1e2130] flex-shrink-0" />

        {/* ================================================= */}
        {/* SCROLLABLE NAVIGATION */}
        {/* ================================================= */}
        <div
          className="
            flex-1
            min-h-0
            overflow-y-auto
            overflow-x-hidden
            py-1
            scrollbar-thin
            scrollbar-track-transparent
            scrollbar-thumb-[#2a2e3d]
            hover:scrollbar-thumb-[#3a3f52]
          "
        >
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {/* ================= NAV ITEMS ================= */}
                {!routesLoading &&
                  navItems.map((action) => {
                    const ActionIcon = action.icon;
                    const isActive = location.pathname === action.route;

                    return (
                      <SidebarMenuItem key={action.id}>
                        <SidebarMenuButton
                          asChild
                          isActive={isActive}
                          tooltip={action.title}
                          className={`
                            mx-1
                            rounded-lg
                            transition-colors
                            ${isActive ? activeClass : inactiveClass}
                          `}
                        >
                          <NavLink
                            to={action.route}
                            end={action.route === "/dashboard"}
                            className="flex items-center gap-2 w-full"
                          >
                            <ActionIcon
                              className={`
                                h-4
                                w-4
                                flex-shrink-0
                                ${isActive ? "text-white" : "text-slate-200"}
                              `}
                            />
                            {!collapsed && <span>{action.title}</span>}
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}

                {/* ================= ORG DOCUMENTS — ADMIN ONLY ================= */}
                {isAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isOrgDocsActive}
                      tooltip="Org Documents"
                      onClick={handleOrgDocsToggle}
                      className={`
                        mx-1
                        rounded-lg
                        transition-colors
                        cursor-pointer
                        ${isOrgDocsActive ? activeClass : inactiveClass}
                      `}
                    >
                      <FileText
                        className={`
                          h-4
                          w-4
                          flex-shrink-0
                          ${isOrgDocsActive ? "text-white" : "text-slate-200"}
                        `}
                      />

                      {!collapsed && (
                        <>
                          <span className="flex-1">Org Documents Upload</span>
                          <ChevronDown
                            className={`
                              h-3.5
                              w-3.5
                              text-slate-200
                              transition-transform
                              duration-200
                              ${orgDocsOpen ? "rotate-180" : ""}
                            `}
                          />
                        </>
                      )}
                    </SidebarMenuButton>

                    {/* Document tree */}
                    {!collapsed && orgDocsOpen && (
                      <div className="ml-4 mt-0.5 border-l border-[#2a2e3d] pl-2 space-y-0.5">
                        {docTitles.length === 0 ? (
                          <p className="text-xs text-slate-300 px-2 py-1.5">
                            Loading...
                          </p>
                        ) : (
                          docTitles.map((title) => {
                            const titleExpanded = expandedDocTitleIds.has(
                              title.id,
                            );
                            const titleTypes = docTypes.filter(
                              (t) => t.docs_title_id === title.id,
                            );
                            const isTitleSelected =
                              location.search.includes(`titleId=${title.id}`) &&
                              !location.search.includes("typeId");

                            return (
                              <div key={title.id}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    toggleDocTitle(title.id);
                                    selectDocScope({ titleId: title.id });
                                  }}
                                  className={`
                                    w-full
                                    flex
                                    items-center
                                    gap-1.5
                                    px-2
                                    py-1.5
                                    rounded-md
                                    text-xs
                                    transition-colors
                                    ${isTitleSelected ? subActiveClass : subInactiveClass}
                                  `}
                                >
                                  {titleExpanded ? (
                                    <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                  )}
                                  <span className="flex-1 text-left truncate">
                                    {title.name}
                                  </span>
                                </button>

                                {titleExpanded && (
                                  <div className="ml-3 border-l border-[#2a2e3d] pl-2 space-y-0.5">
                                    {titleTypes.map((type) => {
                                      const subs = docSubTypes.filter(
                                        (s) =>
                                          s.docs_type_id === type.id &&
                                          s.docs_title_id === title.id,
                                      );
                                      const typeExpanded =
                                        expandedDocTypeIds.has(type.id);
                                      const isTypeSelected =
                                        location.search.includes(
                                          `typeId=${type.id}`,
                                        ) && !location.search.includes("subId");

                                      return (
                                        <div key={type.id}>
                                          <button
                                            type="button"
                                            onClick={() => {
                                              if (subs.length > 0) {
                                                toggleDocType(type.id);
                                              }
                                              selectDocScope({
                                                titleId: title.id,
                                                typeId: type.id,
                                              });
                                            }}
                                            className={`
                                              w-full
                                              flex
                                              items-center
                                              gap-1.5
                                              px-2
                                              py-1.5
                                              rounded-md
                                              text-xs
                                              transition-colors
                                              ${isTypeSelected ? subActiveClass : subInactiveClass}
                                            `}
                                          >
                                            {subs.length > 0 ? (
                                              typeExpanded ? (
                                                <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                              ) : (
                                                <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                              )
                                            ) : (
                                              <span className="w-3 flex-shrink-0" />
                                            )}
                                            <span className="flex-1 text-left truncate">
                                              {type.name}
                                            </span>
                                          </button>

                                          {subs.length > 0 && typeExpanded && (
                                            <div className="ml-3 border-l border-[#2a2e3d] pl-2 space-y-0.5">
                                              {subs.map((sub) => {
                                                const isSubSelected =
                                                  location.search.includes(
                                                    `subId=${sub.id}`,
                                                  );
                                                return (
                                                  <button
                                                    type="button"
                                                    key={sub.id}
                                                    onClick={() =>
                                                      selectDocScope({
                                                        titleId: title.id,
                                                        typeId: type.id,
                                                        subId: sub.id,
                                                      })
                                                    }
                                                    className={`
                                                      w-full
                                                      flex
                                                      items-center
                                                      px-2
                                                      py-1.5
                                                      rounded-md
                                                      text-xs
                                                      transition-colors
                                                      ${isSubSelected ? subActiveClass : subInactiveClass}
                                                    `}
                                                  >
                                                    <span className="w-3 flex-shrink-0" />
                                                    <span className="flex-1 text-left truncate">
                                                      {sub.name}
                                                    </span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </SidebarMenuItem>
                )}

                {/* ================= SETTINGS — ADMIN ONLY ================= */}
                {isAdmin && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      isActive={isSettingsActive}
                      tooltip="Settings"
                      onClick={() => {
                        if (!collapsed) {
                          setSettingsOpen((v) => !v);
                        }
                      }}
                      className={`
                        mx-1
                        rounded-lg
                        transition-colors
                        cursor-pointer
                        ${isSettingsActive ? activeClass : inactiveClass}
                      `}
                    >
                      <Settings className="h-4 w-4 flex-shrink-0 text-slate-200" />

                      {!collapsed && (
                        <>
                          <span className="flex-1">Settings</span>
                          <ChevronDown
                            className={`
                              h-3.5
                              w-3.5
                              text-slate-200
                              transition-transform
                              duration-200
                              ${settingsOpen ? "rotate-180" : ""}
                            `}
                          />
                        </>
                      )}
                    </SidebarMenuButton>

                    {/* Settings tree */}
                    {!collapsed && settingsOpen && (
                      <div className="ml-4 mt-0.5 border-l border-[#2a2e3d] pl-2 space-y-0.5">
                        {settingsItems.map((item) => {
                          const ItemIcon = item.icon;
                          const hasChildren =
                            item.children && item.children.length > 0;

                          if (hasChildren) {
                            const childActive = item.children.some((child) =>
                              isSettingsRouteActive(child.route),
                            );

                            return (
                              <div key={item.id}>
                                <button
                                  type="button"
                                  onClick={() => setPricingOpen((v) => !v)}
                                  className={`
                                    w-full
                                    flex
                                    items-center
                                    gap-1.5
                                    px-2
                                    py-1.5
                                    rounded-md
                                    text-xs
                                    transition-colors
                                    ${childActive ? subActiveClass : subInactiveClass}
                                  `}
                                >
                                  {pricingOpen ? (
                                    <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                  ) : (
                                    <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-200" />
                                  )}
                                  <ItemIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-200" />
                                  <span className="flex-1 text-left truncate">
                                    {item.title}
                                  </span>
                                </button>

                                {pricingOpen && (
                                  <div className="ml-3 border-l border-[#2a2e3d] pl-2 space-y-0.5">
                                    {item.children.map((child) => {
                                      const isChildSelected =
                                        isSettingsRouteActive(child.route);
                                      return (
                                        <button
                                          type="button"
                                          key={child.id}
                                          onClick={() => navigate(child.route)}
                                          className={`
                                            w-full
                                            flex
                                            items-center
                                            px-2
                                            py-1.5
                                            rounded-md
                                            text-xs
                                            transition-colors
                                            ${isChildSelected ? subActiveClass : subInactiveClass}
                                          `}
                                        >
                                          <span className="w-3 flex-shrink-0" />
                                          <span className="flex-1 text-left truncate">
                                            {child.title}
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          const isItemSelected = isSettingsRouteActive(
                            item.route,
                          );

                          return (
                            <button
                              type="button"
                              key={item.id}
                              onClick={() => navigate(item.route)}
                              className={`
                                w-full
                                flex
                                items-center
                                gap-1.5
                                px-2
                                py-1.5
                                rounded-md
                                text-xs
                                transition-colors
                                ${isItemSelected ? subActiveClass : subInactiveClass}
                              `}
                            >
                              <span className="w-3 flex-shrink-0" />
                              <ItemIcon className="h-3.5 w-3.5 flex-shrink-0 text-slate-200" />
                              <span className="flex-1 text-left truncate">
                                {item.title}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </div>
      </SidebarContent>

      <SidebarFooter className="bg-[#0f1117] flex-shrink-0" />
    </Sidebar>
  );
}

export default AppSidebar;
